import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Label from "../../../components/BaseUI/Label";
import Checkbox from "../../../components/BaseUI/Checkbox";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  FiPlus,
  FiTrash2,
  FiClock,
  FiArrowLeft,
  FiSave,
  FiCalendar,
  FiList,
  FiEdit2,
  FiX,
} from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import "./Shifts.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const DAYS = [
  { key: "monday", short: "Pzt", full: "Pazartesi" },
  { key: "tuesday", short: "Sal", full: "Salı" },
  { key: "wednesday", short: "Çar", full: "Çarşamba" },
  { key: "thursday", short: "Per", full: "Perşembe" },
  { key: "friday", short: "Cum", full: "Cuma" },
  { key: "saturday", short: "Cmt", full: "Cumartesi" },
  { key: "sunday", short: "Paz", full: "Pazar" },
];

const DEFAULT_WEEKDAYS = new Set([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
]);

const emptyBreak = () => ({
  name: "",
  startTime: "12:00",
  endTime: "12:30",
});

const defaultDayRows = () =>
  DAYS.map((d) => ({
    day: d.key,
    enabled: DEFAULT_WEEKDAYS.has(d.key),
    startTime: "08:00",
    endTime: "17:00",
    breaks: [],
  }));

const rowsFromApiSchedules = (schedules) => {
  const byDay = Object.fromEntries((schedules || []).map((s) => [s.day, s]));
  return DAYS.map((d) => {
    const s = byDay[d.key];
    if (s) {
      return {
        day: d.key,
        enabled: true,
        startTime: s.startTime || "08:00",
        endTime: s.endTime || "17:00",
        breaks: (s.breaks || []).map((b) => ({
          name: b.name || "",
          startTime: b.startTime || "12:00",
          endTime: b.endTime || "12:30",
        })),
      };
    }
    return {
      day: d.key,
      enabled: false,
      startTime: "08:00",
      endTime: "17:00",
      breaks: [],
    };
  });
};

const toastAppearance = (type) => {
  if (type === "error") return "danger";
  return type || "success";
};

const ShiftForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  useTitle(
    isEdit ? "Rovion MES - Vardiya Düzenle" : "Rovion MES - Yeni Vardiya",
  );

  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [toleranceMinutes, setToleranceMinutes] = useState(0);
  const [dayRows, setDayRows] = useState(defaultDayRows);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [toasts, setToasts] = useState([]);
  /** day key or null */
  const [addBreakDay, setAddBreakDay] = useState(null);
  const [breakDraft, setBreakDraft] = useState(emptyBreak);
  /** day key or null — list breaks for that day */
  const [viewBreaksDay, setViewBreaksDay] = useState(null);
  /** day key or null — bulk add breaks for that day */
  const [bulkBreakDay, setBulkBreakDay] = useState(null);
  const [bulkBreakText, setBulkBreakText] = useState("");
  const [bulkBreakDrafts, setBulkBreakDrafts] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignSelectedDays, setBulkAssignSelectedDays] = useState(
    () => new Set(),
  );
  const [bulkAssignTime, setBulkAssignTime] = useState({
    startTime: "08:00",
    endTime: "17:00",
  });
  const [bulkAssignTab, setBulkAssignTab] = useState("time"); // 'time' | 'breaks'
  const [bulkAssignTimeDirty, setBulkAssignTimeDirty] = useState(false);
  const [bulkAssignBreakDraft, setBulkAssignBreakDraft] = useState(emptyBreak);
  const [bulkAssignBreaks, setBulkAssignBreaks] = useState([]);
  const [editBreakCtx, setEditBreakCtx] = useState(null);
  const [editBreakDraft, setEditBreakDraft] = useState(emptyBreak);

  const dismissToast = useCallback((toastId) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  const addToast = (message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  };

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    axios
      .get(`${API_URL}/api/shifts/${id}`, authHeaders())
      .then(({ data }) => {
        const s = data.shift;
        setName(s.name || "");
        setIsActive(s.isActive !== undefined ? s.isActive : true);
        setToleranceMinutes(
          Number.isFinite(Number(s.toleranceMinutes))
            ? Math.max(0, Math.trunc(Number(s.toleranceMinutes)))
            : 0,
        );
        const schedules = s.daySchedules?.length ? s.daySchedules : null;
        if (schedules) {
          setDayRows(rowsFromApiSchedules(schedules));
        } else {
          setDayRows(defaultDayRows());
        }
      })
      .catch(() => addToast("Vardiya yüklenemedi", "danger"))
      .finally(() => setFetching(false));
  }, [id, isEdit]);

  const setRow = (day, patch) => {
    setDayRows((prev) =>
      prev.map((r) => (r.day === day ? { ...r, ...patch } : r)),
    );
  };

  const openAddBreakModal = (day) => {
    setBreakDraft(emptyBreak());
    setAddBreakDay(day);
  };

  const closeAddBreakModal = () => {
    setAddBreakDay(null);
    setBreakDraft(emptyBreak());
  };

  const confirmAddBreak = () => {
    if (!addBreakDay) return;
    const name = String(breakDraft.name || "").trim();
    if (!name) {
      addToast("Mola adı zorunludur", "danger");
      return;
    }
    if (!breakDraft.startTime || !breakDraft.endTime) {
      addToast("Mola başlangıç ve bitiş saati girin", "danger");
      return;
    }
    setDayRows((prev) =>
      prev.map((r) =>
        r.day === addBreakDay
          ? {
              ...r,
              breaks: [
                ...r.breaks,
                {
                  name,
                  startTime: breakDraft.startTime,
                  endTime: breakDraft.endTime,
                },
              ],
            }
          : r,
      ),
    );
    closeAddBreakModal();
  };

  const removeBreak = (day, index) => {
    setDayRows((prev) =>
      prev.map((r) =>
        r.day === day
          ? { ...r, breaks: r.breaks.filter((_, i) => i !== index) }
          : r,
      ),
    );
  };

  const openEditBreakModal = (day, index) => {
    const row = dayRows.find((r) => r.day === day);
    const br = row?.breaks?.[index];
    if (!br) return;
    setEditBreakCtx({ day, index });
    setEditBreakDraft({
      name: br.name || "",
      startTime: br.startTime || "12:00",
      endTime: br.endTime || "12:30",
    });
  };

  const closeEditBreakModal = () => {
    setEditBreakCtx(null);
    setEditBreakDraft(emptyBreak());
  };

  const confirmEditBreak = () => {
    if (!editBreakCtx?.day) return;
    const { day, index } = editBreakCtx;
    const name = String(editBreakDraft.name || "").trim();
    if (!name) return addToast("Mola adı zorunludur", "danger");
    if (!editBreakDraft.startTime || !editBreakDraft.endTime) {
      return addToast("Mola başlangıç ve bitiş saati girin", "danger");
    }
    setDayRows((prev) =>
      prev.map((r) => {
        if (r.day !== day) return r;
        const next = [...(r.breaks || [])];
        const i = Number(index);
        if (!Number.isFinite(i) || !next[i]) return r;
        next[i] = {
          name,
          startTime: editBreakDraft.startTime,
          endTime: editBreakDraft.endTime,
        };
        return { ...r, breaks: next };
      }),
    );
    closeEditBreakModal();
  };

  const openBulkAssignModal = () => {
    setBulkAssignTime({ startTime: "08:00", endTime: "17:00" });
    setBulkAssignTimeDirty(false);
    setBulkAssignSelectedDays(new Set());
    setBulkAssignTab("time");
    setBulkAssignBreakDraft(emptyBreak());
    setBulkAssignBreaks([]);
    setBulkAssignOpen(true);
  };

  const closeBulkAssignModal = () => {
    setBulkAssignOpen(false);
    setBulkAssignSelectedDays(new Set());
    setBulkAssignTime({ startTime: "08:00", endTime: "17:00" });
    setBulkAssignTimeDirty(false);
    setBulkAssignTab("time");
    setBulkAssignBreakDraft(emptyBreak());
    setBulkAssignBreaks([]);
  };

  const setBulkAssignDayChecked = (dayKey, checked) => {
    setBulkAssignSelectedDays((prev) => {
      const next = new Set(prev);
      if (checked) next.add(dayKey);
      else next.delete(dayKey);
      return next;
    });
  };

  const collectBulkAssignBreaks = () => {
    const list = [...bulkAssignBreaks];
    const nm = String(bulkAssignBreakDraft.name || "").trim();
    // Only validate draft when user started a break (name); ignore default times
    if (!nm) return list;
    if (!bulkAssignBreakDraft.startTime || !bulkAssignBreakDraft.endTime) {
      addToast("Mola başlangıç ve bitiş saati girin", "danger");
      return null;
    }
    list.push({
      name: nm,
      startTime: bulkAssignBreakDraft.startTime,
      endTime: bulkAssignBreakDraft.endTime,
    });
    return list;
  };

  const addBulkAssignBreak = () => {
    const nm = String(bulkAssignBreakDraft.name || "").trim();
    if (!nm) return addToast("Mola adı zorunludur", "danger");
    if (!bulkAssignBreakDraft.startTime || !bulkAssignBreakDraft.endTime) {
      return addToast("Mola başlangıç ve bitiş saati girin", "danger");
    }
    setBulkAssignBreaks((prev) => [
      ...prev,
      {
        name: nm,
        startTime: bulkAssignBreakDraft.startTime,
        endTime: bulkAssignBreakDraft.endTime,
      },
    ]);
    setBulkAssignBreakDraft(emptyBreak());
  };

  const removeBulkAssignBreak = (idx) => {
    setBulkAssignBreaks((prev) => prev.filter((_, i) => i !== idx));
  };

  const confirmBulkAssignApply = () => {
    const days = Array.from(bulkAssignSelectedDays || []);
    if (days.length === 0) return addToast("En az bir gün seçin", "danger");

    const breaksToApply = collectBulkAssignBreaks();
    if (breaksToApply === null) return;

    const shouldApplyTime =
      bulkAssignTimeDirty || bulkAssignTab === "time";

    if (shouldApplyTime) {
      if (!bulkAssignTime.startTime || !bulkAssignTime.endTime) {
        return addToast("Başlangıç ve bitiş saatini girin", "danger");
      }
    } else if (breaksToApply.length === 0) {
      return addToast("Saat veya mola bilgisi girin", "danger");
    }

    setDayRows((prev) =>
      prev.map((r) => {
        if (!days.includes(r.day)) return r;
        const next = { ...r, enabled: true };
        if (shouldApplyTime) {
          next.startTime = bulkAssignTime.startTime;
          next.endTime = bulkAssignTime.endTime;
        }
        if (breaksToApply.length > 0) {
          next.breaks = [...r.breaks, ...breaksToApply];
        }
        return next;
      }),
    );
    addToast(`${days.length} güne uygulandı`, "success");
    closeBulkAssignModal();
  };

  const parseTime = (v) => {
    const s = String(v || "").trim();
    if (!/^\d{2}:\d{2}$/.test(s)) return null;
    const [hh, mm] = s.split(":").map((x) => Number(x));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return s;
  };

  const openBulkBreakModal = (day) => {
    setBulkBreakDay(day);
    setBulkBreakText("");
    setBulkBreakDrafts([]);
  };

  const closeBulkBreakModal = () => {
    setBulkBreakDay(null);
    setBulkBreakText("");
    setBulkBreakDrafts([]);
  };

  const parseBulkBreakLine = (line) => {
    const raw = String(line || "").trim();
    if (!raw) return null;

    // Supported formats:
    // 1) 12:00-12:30 Öğle
    // 2) 12:00 - 12:30 | Öğle
    // 3) Öğle,12:00,12:30
    // 4) Öğle;12:00;12:30

    const m = raw.match(
      /^(\d{2}:\d{2})\s*[-–]\s*(\d{2}:\d{2})(?:\s*[|,;]\s*(.+))?$/,
    );
    if (m) {
      const startTime = parseTime(m[1]);
      const endTime = parseTime(m[2]);
      const name = String(m[3] || "").trim() || "Mola";
      if (!startTime || !endTime) return { error: "Saat formatı hatalı" };
      return { name, startTime, endTime };
    }

    const parts = raw
      .split(/[;,]/)
      .map((x) => String(x).trim())
      .filter(Boolean);
    if (parts.length >= 3) {
      const name = parts[0] || "Mola";
      const startTime = parseTime(parts[1]);
      const endTime = parseTime(parts[2]);
      if (!startTime || !endTime) return { error: "Saat formatı hatalı" };
      return { name, startTime, endTime };
    }

    return { error: "Satır formatı tanınmadı" };
  };

  const rebuildBulkDrafts = (text) => {
    const lines = String(text || "")
      .split(/\r?\n/)
      .map((l) => String(l).trim())
      .filter(Boolean);

    const drafts = lines.map((line) => {
      const r = parseBulkBreakLine(line);
      if (!r) return { name: "", startTime: "", endTime: "", _error: "" };
      if (r.error) {
        return { name: "", startTime: "", endTime: "", _error: r.error };
      }
      return {
        name: r.name || "",
        startTime: r.startTime || "",
        endTime: r.endTime || "",
        _error: "",
      };
    });
    setBulkBreakDrafts(drafts);
  };

  const confirmBulkAddBreaks = () => {
    if (!bulkBreakDay) return;
    const drafts = Array.isArray(bulkBreakDrafts) ? bulkBreakDrafts : [];
    if (drafts.length === 0) {
      addToast("Mola metni boş", "danger");
      return;
    }

    const toAdd = [];
    const errors = [];
    drafts.forEach((d, idx) => {
      const nm = String(d?.name || "").trim();
      const st = parseTime(d?.startTime);
      const et = parseTime(d?.endTime);
      if (String(d?._error || "").trim()) {
        errors.push(`${idx + 1}. satır: ${String(d._error).trim()}`);
        return;
      }
      if (!nm) {
        errors.push(`${idx + 1}. satır: Mola adı boş`);
        return;
      }
      if (!st || !et) {
        errors.push(`${idx + 1}. satır: Saat formatı hatalı`);
        return;
      }
      toAdd.push({ name: nm, startTime: st, endTime: et });
    });

    if (errors.length) {
      addToast(errors.slice(0, 2).join(" • "), "danger");
      return;
    }

    setDayRows((prev) =>
      prev.map((r) =>
        r.day === bulkBreakDay ? { ...r, breaks: [...r.breaks, ...toAdd] } : r,
      ),
    );

    addToast(`${toAdd.length} mola eklendi`, "success");
    closeBulkBreakModal();
  };

  // multi-day break + bulk time are merged into bulkAssign modal

  const buildPayload = () => {
    const daySchedules = dayRows
      .filter((r) => r.enabled)
      .map((r) => ({
        day: r.day,
        startTime: r.startTime,
        endTime: r.endTime,
        breaks: r.breaks
          .filter((b) => String(b.name || "").trim())
          .map((b) => ({
            name: String(b.name).trim(),
            startTime: b.startTime,
            endTime: b.endTime,
          })),
      }));
    return {
      name: name.trim(),
      isActive,
      toleranceMinutes: Number.isFinite(Number(toleranceMinutes))
        ? Math.max(0, Math.trunc(Number(toleranceMinutes)))
        : 0,
      daySchedules,
    };
  };

  const handleSave = async () => {
    if (!name.trim()) return addToast("Vardiya adı zorunludur", "danger");
    const payload = buildPayload();
    if (payload.daySchedules.length === 0) {
      return addToast("En az bir gün seçili olmalıdır", "danger");
    }
    for (const d of payload.daySchedules) {
      if (!d.startTime || !d.endTime) {
        return addToast(
          `${DAYS.find((x) => x.key === d.day)?.full || d.day}: saatleri doldurun`,
          "danger",
        );
      }
      for (const b of d.breaks) {
        if (!b.startTime || !b.endTime) {
          return addToast(
            `${d.day} molaları: tüm molalarda saat aralığı girin`,
            "danger",
          );
        }
      }
    }

    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/shifts/${id}`, payload, authHeaders());
        addToast("Vardiya güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/shifts`, payload, authHeaders());
        addToast("Vardiya oluşturuldu", "success");
      }
      setTimeout(() => navigate("/shifts"), 800);
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setLoading(false);
    }
  };

  const cardTitle = isEdit ? "Vardiyayı güncelleyin" : "Yeni vardiya tanımı";

  const addBreakDayFull =
    addBreakDay &&
    (DAYS.find((d) => d.key === addBreakDay)?.full || addBreakDay);
  const viewBreaksDayFull =
    viewBreaksDay &&
    (DAYS.find((d) => d.key === viewBreaksDay)?.full || viewBreaksDay);
  const viewBreaksList = viewBreaksDay
    ? dayRows.find((r) => r.day === viewBreaksDay)?.breaks || []
    : [];
  const bulkBreakDayFull =
    bulkBreakDay &&
    (DAYS.find((d) => d.key === bulkBreakDay)?.full || bulkBreakDay);

  return (
    <AppPage
      title={isEdit ? "Vardiyayı Düzenle" : "Yeni Vardiya"}
      description="Her gün için ayrı çalışma saatleri ve molalar tanımlayabilirsiniz"
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={toastAppearance(t.type)}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal isOpen={!!addBreakDay} onClose={closeAddBreakModal} width="small">
        <ModalHeader>
          Mola ekle{addBreakDayFull ? ` — ${addBreakDayFull}` : ""}
        </ModalHeader>
        <ModalBody showCloseButton={false}>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label className="mb-0">Mola adı *</Label>
              <Input
                value={breakDraft.name}
                onChange={(v) => setBreakDraft((d) => ({ ...d, name: v }))}
                placeholder="örn. Öğle molası"
              />
            </div>
            <div className="d-flex gap-2">
              <div className="flex-grow-1">
                <Label className="mb-0 small text-muted">Başlangıç *</Label>
                <Input
                  type="time"
                  value={breakDraft.startTime}
                  onChange={(v) =>
                    setBreakDraft((d) => ({ ...d, startTime: v }))
                  }
                />
              </div>
              <div className="flex-grow-1">
                <Label className="mb-0 small text-muted">Bitiş *</Label>
                <Input
                  type="time"
                  value={breakDraft.endTime}
                  onChange={(v) => setBreakDraft((d) => ({ ...d, endTime: v }))}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button appearance="danger-subtle" onClick={closeAddBreakModal}>
            İptal
          </Button>
          <Button appearance="primary-animated" onClick={confirmAddBreak}>
            Ekle
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!viewBreaksDay}
        onClose={() => setViewBreaksDay(null)}
        width="medium"
      >
        <ModalHeader>
          Molalar{viewBreaksDayFull ? ` — ${viewBreaksDayFull}` : ""}
        </ModalHeader>
        <ModalBody showCloseButton={false}>
          {viewBreaksList.length === 0 ? (
            <p className="text-muted mb-0 small">
              Bu gün için tanımlı mola yok. Sağ üstteki + ile ekleyebilirsiniz.
            </p>
          ) : (
            <ul className="list-unstyled mb-0 shift-form-page__breaks-modal-list">
              {viewBreaksList.map((br, i) => (
                <li
                  key={`${viewBreaksDay}-${i}`}
                  className="d-flex align-items-center justify-content-between gap-2 py-2 border-bottom"
                >
                  <div>
                    <div className="weight-500">{br.name}</div>
                    <div className="small text-muted">
                      {br.startTime} – {br.endTime}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <IconButton
                      icon={<FiEdit2 />}
                      label="Molayı düzenle"
                      appearance="subtle"
                      shape="circle"
                      onClick={() => {
                        if (!viewBreaksDay) return;
                        const dayKey = viewBreaksDay;
                        setViewBreaksDay(null);
                        // open after list modal closes, otherwise it can render behind
                        setTimeout(() => openEditBreakModal(dayKey, i), 0);
                      }}
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      label="Molayı sil"
                      appearance="subtle"
                      shape="circle"
                      onClick={() => {
                        if (viewBreaksDay) removeBreak(viewBreaksDay, i);
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex gap-2 justify-content-end w-100">
            <Button
              appearance="primary-subtle"
              onClick={() => {
                if (!viewBreaksDay) return;
                const dayKey = viewBreaksDay;
                setViewBreaksDay(null);
                // open after list modal closes, otherwise it can render behind
                setTimeout(() => openAddBreakModal(dayKey), 0);
              }}
            >
              Mola ekle
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() => setViewBreaksDay(null)}
            >
              Kapat
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!bulkBreakDay}
        onClose={closeBulkBreakModal}
        width="medium"
      >
        <ModalHeader>
          Mola Ekle{bulkBreakDayFull ? ` - ${bulkBreakDayFull}` : ""}
        </ModalHeader>
        <ModalBody showCloseButton={false}>
          <div className="d-flex flex-column gap-2">
            {bulkBreakDay && (
              <div className="d-flex flex-column gap-2">
                <Label className="mb-0">Mevcut molalar</Label>
                {(dayRows.find((r) => r.day === bulkBreakDay)?.breaks || [])
                  .length === 0 ? (
                  <div className="small text-muted">Bu gün için mola yok.</div>
                ) : (
                  <div className="d-flex flex-column gap-1">
                    {(
                      dayRows.find((r) => r.day === bulkBreakDay)?.breaks || []
                    ).map((br, i) => (
                      <div
                        key={`existing-${bulkBreakDay}-${i}`}
                        className="d-flex align-items-center justify-content-between gap-2 py-1"
                      >
                        <div className="small">
                          <b>{br.name}</b>{" "}
                          <span className="text-muted">
                            ({br.startTime} – {br.endTime})
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <IconButton
                            icon={<FiEdit2 />}
                            label="Molayı düzenle"
                            appearance="subtle"
                            shape="circle"
                            onClick={() => openEditBreakModal(bulkBreakDay, i)}
                          />
                          <IconButton
                            icon={<FiTrash2 />}
                            label="Molayı sil"
                            appearance="subtle"
                            shape="circle"
                            onClick={() => removeBreak(bulkBreakDay, i)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <p className="text-muted small mb-1">
              Her satıra 1 mola yazın. Örnek: <code>12:00-12:30 Öğle</code> veya{" "}
              <code>Öğle,12:00,12:30</code>
            </p>
            <textarea
              className="form-control"
              rows={8}
              placeholder={"12:00-12:10 Çay\n12:30-13:00 Öğle"}
              value={bulkBreakText}
              onChange={(e) => {
                const t = e.target.value;
                setBulkBreakText(t);
                rebuildBulkDrafts(t);
              }}
            />
            {bulkBreakDrafts.length > 0 && (
              <div className="d-flex flex-column gap-2">
                <Label className="mb-0">Önizleme / Düzenle</Label>
                <div className="d-flex flex-column gap-2">
                  {bulkBreakDrafts.map((d, idx) => (
                    <div
                      key={`bulk-${idx}`}
                      className="d-flex align-items-end gap-2 flex-wrap"
                    >
                      <div style={{ width: 48 }} className="small text-muted">
                        {idx + 1}.
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 200 }}>
                        <Label className="mb-0 small text-muted">Ad</Label>
                        <Input
                          value={d.name}
                          onChange={(v) =>
                            setBulkBreakDrafts((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, name: v, _error: "" } : x,
                              ),
                            )
                          }
                        />
                      </div>
                      <div style={{ minWidth: 140 }}>
                        <Label className="mb-0 small text-muted">Başl.</Label>
                        <Input
                          type="time"
                          value={d.startTime}
                          onChange={(v) =>
                            setBulkBreakDrafts((prev) =>
                              prev.map((x, i) =>
                                i === idx
                                  ? { ...x, startTime: v, _error: "" }
                                  : x,
                              ),
                            )
                          }
                        />
                      </div>
                      <div style={{ minWidth: 140 }}>
                        <Label className="mb-0 small text-muted">Bit.</Label>
                        <Input
                          type="time"
                          value={d.endTime}
                          onChange={(v) =>
                            setBulkBreakDrafts((prev) =>
                              prev.map((x, i) =>
                                i === idx
                                  ? { ...x, endTime: v, _error: "" }
                                  : x,
                              ),
                            )
                          }
                        />
                      </div>
                      <IconButton
                        icon={<FiTrash2 />}
                        label="Satırı sil"
                        appearance="subtle"
                        shape="circle"
                        onClick={() =>
                          setBulkBreakDrafts((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      />
                      {!!String(d._error || "").trim() && (
                        <div className="small text-danger w-100">
                          {String(d._error).trim()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button appearance="danger-subtle" onClick={closeBulkBreakModal}>
            İptal
          </Button>
          <Button appearance="primary-animated" onClick={confirmBulkAddBreaks}>
            Ekle
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!editBreakCtx}
        onClose={closeEditBreakModal}
        width="small"
      >
        <ModalHeader>Mola düzenle</ModalHeader>
        <ModalBody showCloseButton={false}>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label className="mb-0">Mola adı *</Label>
              <Input
                value={editBreakDraft.name}
                onChange={(v) => setEditBreakDraft((d) => ({ ...d, name: v }))}
              />
            </div>
            <div className="d-flex gap-2">
              <div className="flex-grow-1">
                <Label className="mb-0 small text-muted">Başlangıç *</Label>
                <Input
                  type="time"
                  value={editBreakDraft.startTime}
                  onChange={(v) =>
                    setEditBreakDraft((d) => ({ ...d, startTime: v }))
                  }
                />
              </div>
              <div className="flex-grow-1">
                <Label className="mb-0 small text-muted">Bitiş *</Label>
                <Input
                  type="time"
                  value={editBreakDraft.endTime}
                  onChange={(v) =>
                    setEditBreakDraft((d) => ({ ...d, endTime: v }))
                  }
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button appearance="danger-subtle" onClick={closeEditBreakModal}>
            İptal
          </Button>
          <Button appearance="primary-animated" onClick={confirmEditBreak}>
            Kaydet
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={bulkAssignOpen}
        onClose={closeBulkAssignModal}
        width="medium"
      >
        {/* <ModalHeader></ModalHeader> */}
        <ModalBody showCloseButton={false}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Saat/Mola Ekle</h3>
            <Button appearance="danger-subtle" onClick={closeBulkAssignModal}>
              <FiX size={16} />
            </Button>
          </div>
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column gap-2">
              <Label className="mb-0">Günler *</Label>
              <div className="d-flex flex-wrap gap-2">
                {DAYS.map((d) => {
                  const checked = bulkAssignSelectedDays.has(d.key);
                  return (
                    <div key={d.key} style={{ minWidth: 140 }}>
                      <Checkbox
                        value={d.key}
                        label={d.full}
                        isChecked={checked}
                        onChange={({ checked: nextChecked }) =>
                          setBulkAssignDayChecked(d.key, nextChecked)
                        }
                      />
                    </div>
                  );
                })}
              </div>
              {/* <p className="small text-muted mb-0">
                Seçilen günler otomatik olarak <b>açık</b> yapılır.
              </p> */}
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                appearance={
                  bulkAssignTab === "time"
                    ? "primary-animated"
                    : "primary-subtle"
                }
                onClick={() => setBulkAssignTab("time")}
              >
                Saatler
              </Button>
              <Button
                appearance={
                  bulkAssignTab === "breaks"
                    ? "primary-animated"
                    : "primary-subtle"
                }
                onClick={() => setBulkAssignTab("breaks")}
              >
                Molalar
              </Button>
            </div>

            {bulkAssignTab === "time" ? (
              <div>
                <Label className="mb-0">Çalışma saatleri</Label>
                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <Label className="mb-0 small text-muted">Başlangıç</Label>
                    <Input
                      type="time"
                      value={bulkAssignTime.startTime}
                      onChange={(v) => {
                        setBulkAssignTimeDirty(true);
                        setBulkAssignTime((p) => ({ ...p, startTime: v }));
                      }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <Label className="mb-0 small text-muted">Bitiş</Label>
                    <Input
                      type="time"
                      value={bulkAssignTime.endTime}
                      onChange={(v) => {
                        setBulkAssignTimeDirty(true);
                        setBulkAssignTime((p) => ({ ...p, endTime: v }));
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Label className="mb-0">Molalar</Label>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex flex-column gap-2 border rounded p-2">
                    <div>
                      <Label className="mb-0 small text-muted">Mola adı</Label>
                      <Input
                        value={bulkAssignBreakDraft.name}
                        onChange={(v) =>
                          setBulkAssignBreakDraft((d) => ({ ...d, name: v }))
                        }
                        placeholder="örn. Öğle molası"
                      />
                    </div>
                    <div className="d-flex gap-2">
                      <div className="flex-grow-1">
                        <Label className="mb-0 small text-muted">
                          Başlangıç
                        </Label>
                        <Input
                          type="time"
                          value={bulkAssignBreakDraft.startTime}
                          onChange={(v) =>
                            setBulkAssignBreakDraft((d) => ({
                              ...d,
                              startTime: v,
                            }))
                          }
                        />
                      </div>
                      <div className="flex-grow-1">
                        <Label className="mb-0 small text-muted">Bitiş</Label>
                        <Input
                          type="time"
                          value={bulkAssignBreakDraft.endTime}
                          onChange={(v) =>
                            setBulkAssignBreakDraft((d) => ({
                              ...d,
                              endTime: v,
                            }))
                          }
                        />
                      </div>
                      <div className="d-flex align-items-end">
                        <Button
                          appearance="primary-subtle"
                          onClick={addBulkAssignBreak}
                        >
                          Ekle
                        </Button>
                      </div>
                    </div>
                  </div>

                  {bulkAssignBreaks.length > 0 && (
                    <div className="d-flex flex-column gap-1">
                      {bulkAssignBreaks.map((b, idx) => (
                        <div
                          key={`ba-br-${idx}`}
                          className="d-flex align-items-center justify-content-between gap-2 border rounded p-2"
                        >
                          <div className="small">
                            <b>{b.name}</b>{" "}
                            <span className="text-muted">
                              {b.startTime} – {b.endTime}
                            </span>
                          </div>
                          <IconButton
                            icon={<FiTrash2 />}
                            label="Sil"
                            appearance="subtle"
                            shape="circle"
                            onClick={() => removeBulkAssignBreak(idx)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button appearance="danger-subtle" onClick={closeBulkAssignModal}>
            İptal
          </Button>
          <Button
            appearance="primary-animated"
            onClick={confirmBulkAssignApply}
          >
            Uygula
          </Button>
        </ModalFooter>
      </Modal>

      {fetching ? (
        <CardContainer title="Yükleniyor">
          <div className="shift-form-loading">
            <FiClock size={44} className="spin" />
            <p>Vardiya bilgileri yükleniyor…</p>
          </div>
        </CardContainer>
      ) : (
        <CardContainer>
          <div className="shift-form-page">
            <div className="row g-4">
              <div className="col-lg-4 col-12">
                <div className="card h-100 shadow-sm border-0">
                  <div className="card-body shift-form-page__section p-4">
                    <h5 className="shift-form-page__section-title">
                      <FiClock size={18} />
                      Temel bilgiler
                    </h5>

                    <div>
                      <Label className="mb-0">Vardiya adı *</Label>
                      <Input
                        value={name}
                        onChange={(v) => setName(v)}
                        placeholder="örn. Üretim vardiyası"
                      />
                    </div>

                    <div className="pt-3">
                      <Label className="mb-0">Tolerans (dk)</Label>
                      <Input
                        type="number"
                        value={String(toleranceMinutes)}
                        onChange={(v) => {
                          const n = Number(v);
                          if (!Number.isFinite(n))
                            return setToleranceMinutes(0);
                          const clamped = Math.max(
                            0,
                            Math.min(1440, Math.trunc(n)),
                          );
                          setToleranceMinutes(clamped);
                        }}
                        placeholder="0"
                      />
                      <div className="small text-muted mt-1">
                        Dakika cinsinden esneklik (0–1440).
                      </div>
                    </div>

                    {/* <div className="shift-form-page__active-row pt-3 mt-auto">
                      <Checkbox
                        value="active"
                        label="Vardiya aktif"
                        isChecked={isActive}
                        onChange={({ checked }) => setIsActive(checked)}
                      />
                    </div> */}
                  </div>
                </div>
              </div>

              <div className="col-lg-8 col-12 mt-3">
                <div className="card h-100 shadow-sm border-0">
                  <div className="card-body shift-form-page__section p-3">
                    <h5 className="shift-form-page__section-title shift-form-page__section-title--compact">
                      <FiCalendar size={16} />
                      Haftalık program
                    </h5>
                    <p className="shift-form-page__section-sub">
                      Kutuya tıklayarak günü açıp kapatabilirsiniz. Sağ üstteki
                      liste ile molaları görüntüleyin, + ile yeni mola ekleyin.
                    </p>
                    <div className="d-flex justify-content-end gap-2 mb-2 flex-wrap">
                      <Button
                        appearance="primary-subtle"
                        onClick={openBulkAssignModal}
                      >
                        Saat/Mola Ekle
                      </Button>
                    </div>

                    <div className="shift-form-page__weekly">
                      {dayRows.map((row) => {
                        const dayMeta = DAYS.find((d) => d.key === row.day);
                        const dayFull = dayMeta?.full || row.day;
                        const toggleDay = () =>
                          setRow(row.day, { enabled: !row.enabled });
                        const onDayBlockClick = (e) => {
                          if (e.target.closest("[data-shift-day-interactive]"))
                            return;
                          toggleDay();
                        };
                        const onDayBlockKeyDown = (e) => {
                          if (
                            e.target !== e.currentTarget &&
                            e.target.closest("[data-shift-day-interactive]")
                          ) {
                            return;
                          }
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleDay();
                          }
                        };
                        return (
                          <div
                            key={row.day}
                            className={`shift-form-page__day-block shift-form-page__day-block--toggle ${
                              row.enabled
                                ? ""
                                : "shift-form-page__day-block--muted"
                            }`}
                            onClick={onDayBlockClick}
                            onKeyDown={onDayBlockKeyDown}
                            role="button"
                            tabIndex={0}
                            aria-pressed={row.enabled}
                            aria-label={`${dayFull}: ${row.enabled ? "açık" : "kapalı"}. Açmak veya kapatmak için Enter veya Space.`}
                          >
                            <div className="shift-form-page__day-block-head">
                              <div
                                className="shift-form-page__day-block-toggle"
                                data-shift-day-interactive
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  value={row.day}
                                  label={dayFull}
                                  isChecked={row.enabled}
                                  onChange={({ checked }) =>
                                    setRow(row.day, { enabled: checked })
                                  }
                                />
                              </div>
                              <div
                                className="shift-form-page__day-block-corner"
                                data-shift-day-interactive
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div style={{ position: "relative" }}>
                                  <IconButton
                                    icon={<FiList size={16} />}
                                    label={`${dayFull} — molaları göster`}
                                    tooltip={
                                      row.breaks.length
                                        ? `${row.breaks.length} mola`
                                        : "Molaları göster"
                                    }
                                    appearance="subtle"
                                    shape="circle"
                                    isDisabled={!row.enabled}
                                    onClick={() => setViewBreaksDay(row.day)}
                                  />
                                  {row.enabled && row.breaks.length > 0 && (
                                    <span
                                      style={{
                                        position: "absolute",
                                        top: -4,
                                        right: -4,
                                        minWidth: 16,
                                        height: 16,
                                        padding: "0 5px",
                                        borderRadius: 999,
                                        background: "#e11d48",
                                        color: "#fff",
                                        fontSize: 10,
                                        lineHeight: "16px",
                                        textAlign: "center",
                                        fontWeight: 700,
                                        pointerEvents: "none",
                                      }}
                                      aria-hidden="true"
                                    >
                                      {row.breaks.length}
                                    </span>
                                  )}
                                </div>
                                <IconButton
                                  icon={<FiPlus size={16} />}
                                  label={`${dayFull} — mola ekle`}
                                  tooltip={`${dayFull} — mola ekle`}
                                  appearance="subtle"
                                  shape="circle"
                                  isDisabled={!row.enabled}
                                  onClick={() => openAddBreakModal(row.day)}
                                />
                              </div>
                              <div
                                className="shift-form-page__day-block-times"
                                data-shift-day-interactive
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="shift-form-page__time-field">
                                  <Label className="mb-0 shift-form-page__time-field-label">
                                    Başl.
                                  </Label>
                                  <Input
                                    type="time"
                                    value={row.startTime}
                                    onChange={(v) =>
                                      setRow(row.day, { startTime: v })
                                    }
                                    disabled={!row.enabled}
                                  />
                                </div>
                                <div className="shift-form-page__time-field">
                                  <Label className="mb-0 shift-form-page__time-field-label">
                                    Bit.
                                  </Label>
                                  <Input
                                    type="time"
                                    value={row.endTime}
                                    onChange={(v) =>
                                      setRow(row.day, { endTime: v })
                                    }
                                    disabled={!row.enabled}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContainer>
      )}

      <div className="card card-body border-0" style={{ marginTop: 16 }}>
        <div className="d-flex align-items-center justify-content-end flex-wrap gap-2 p-3">
          <Button
            appearance="danger-subtle"
            onClick={() => navigate("/shifts")}
            icon={<FiArrowLeft />}
            disabled={loading}
          >
            Geri
          </Button>
          <Button
            appearance="primary-animated"
            onClick={handleSave}
            disabled={loading || fetching}
            icon={<FiSave />}
          >
            {loading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </div>
    </AppPage>
  );
};

export default withAuth(ShiftForm);
